import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Mic, Square, RotateCcw, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface VoiceRecorderProps {
  onSave: (audioBlob: Blob) => Promise<void>;
  existingUrl?: string | null;
  className?: string;
}

export function getFileExtension(mimeType: string): string {
  if (mimeType.startsWith('audio/wav') || mimeType.startsWith('audio/wave')) return 'wav';
  if (mimeType.startsWith('audio/webm')) return 'webm';
  if (mimeType.startsWith('audio/ogg')) return 'ogg';
  if (mimeType.startsWith('audio/mp4')) return 'mp4';
  return 'wav';
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(view, 8, 'WAVE');
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(view, 36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    offset += 2;
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

export function VoiceRecorder({ onSave, existingUrl, className }: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(existingUrl || null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const gainRef = useRef<GainNode | null>(null);
  const samplesRef = useRef<Float32Array[]>([]);
  const timerInterval = useRef<number | null>(null);
  const recordingRef = useRef(false);

  useEffect(() => {
    return () => {
      if (timerInterval.current) window.clearInterval(timerInterval.current);
      if (audioUrl && !existingUrl) URL.revokeObjectURL(audioUrl);
      releaseResources();
    };
  }, []);

  const releaseResources = () => {
    if (processorRef.current) {
      processorRef.current.onaudioprocess = null;
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (gainRef.current) {
      gainRef.current.disconnect();
      gainRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    try {
      setError(null);

      if (audioUrl && !existingUrl) URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
      releaseResources();

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const ctx = new AudioContext();
      audioContextRef.current = ctx;

      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;

      // Zero-gain node so captured audio doesn't play through speakers
      const gain = ctx.createGain();
      gain.gain.value = 0;
      gainRef.current = gain;

      const processor = ctx.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      samplesRef.current = [];
      recordingRef.current = true;

      processor.onaudioprocess = (e) => {
        if (recordingRef.current) {
          samplesRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0)));
        }
      };

      source.connect(processor);
      processor.connect(gain);
      gain.connect(ctx.destination);

      setIsRecording(true);
      setDuration(0);

      timerInterval.current = window.setInterval(() => {
        setDuration(d => d + 1);
      }, 1000);
    } catch {
      setError('Could not access microphone. Please check your permissions.');
    }
  };

  const stopRecording = async () => {
    if (!recordingRef.current) return;

    recordingRef.current = false;
    setIsRecording(false);
    if (timerInterval.current) window.clearInterval(timerInterval.current);

    const sampleRate = audioContextRef.current?.sampleRate || 44100;

    const totalLength = samplesRef.current.reduce((acc, c) => acc + c.length, 0);
    if (totalLength === 0) {
      releaseResources();
      setError('No audio was captured. Please check your microphone.');
      return;
    }

    const merged = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of samplesRef.current) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }
    samplesRef.current = [];

    releaseResources();

    const wavBlob = encodeWav(merged, sampleRate);
    const url = URL.createObjectURL(wavBlob);
    setAudioUrl(url);

    try {
      await onSave(wavBlob);
    } catch {
      setError('Failed to save audio recording');
    }
  };

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (isRecording) {
    return (
      <div className={cn("space-y-3", className)}>
        <button
          type="button"
          onClick={stopRecording}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
        >
          <Square className="h-4 w-4" />
          Stop Recording
        </button>
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span>Recording {formatDuration(duration)}</span>
        </div>
      </div>
    );
  }

  if (audioUrl) {
    return (
      <div className={cn("space-y-3", className)}>
        {error && (
          <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
          <CheckCircle className="h-4 w-4 flex-shrink-0" />
          <span>Voice response recorded</span>
        </div>

        <audio controls src={audioUrl} className="w-full h-10" preload="auto" />

        <button
          type="button"
          onClick={startRecording}
          className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Re-record
        </button>
      </div>
    );
  }

  return (
    <div className={cn(className)}>
      {error && (
        <div className="mb-3 bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded text-sm">
          {error}
        </div>
      )}

      <Button
        type="button"
        variant="outline"
        onClick={startRecording}
        className="gap-2"
      >
        <Mic className="h-4 w-4" />
        Record voice response
      </Button>
    </div>
  );
}
